import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getApiLanguage } from "src/services/commonLanguageHeader";
import {
  createTemperatureSensorType,
  deleteTemperatureSensorType,
  getTemperatureSensorTypeById,
  getTemperatureSensorTypesList,
  updateTemperatureSensorType,
  updateTemperatureSensorTypeStatus,
  type TemperatureSensorTypeListParams,
  type TemperatureSensorTypePayload,
  type TemperatureSensorTypeStatusPayload,
} from "src/services/administrator/temperatureSensorTypeService";
import { useGeneralRefetchOverride } from "src/queries/general.queries";
import { QueryNames } from "src/queries/queries.constant";

export const useQueryGetTemperatureSensorTypesList = (
  params: TemperatureSensorTypeListParams = {},
  options: { enabled?: boolean } & Record<string, unknown> = {},
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.temperatureSensorTypeList, params, language],
      queryFn: () => getTemperatureSensorTypesList(params, language),
      refetchOnMount: true,
      retry: false,
      ...options,
    }),
    options.enabled !== false,
  );
};

export const useQueryGetTemperatureSensorTypeById = (
  id?: string | number,
  options: { enabled?: boolean } & Record<string, unknown> = {},
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useQuery({
    queryKey: [...QueryNames.temperatureSensorTypeDetail, id, language],
    queryFn: () => getTemperatureSensorTypeById(id!, language),
    enabled: Boolean(id) && options.enabled !== false,
    refetchOnMount: "always",
    retry: false,
    staleTime: 0,
    ...options,
  });
};

export const useMutationCreateTemperatureSensorType = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (payload: TemperatureSensorTypePayload) =>
      createTemperatureSensorType(payload, language),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: QueryNames.temperatureSensorTypeList,
      }),
  });
};

export const useMutationUpdateTemperatureSensorType = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string | number;
      payload: TemperatureSensorTypePayload & { id: string | number };
    }) => updateTemperatureSensorType(id, payload, language),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: QueryNames.temperatureSensorTypeList,
        }),
        queryClient.invalidateQueries({
          queryKey: QueryNames.temperatureSensorTypeDetail,
          refetchType: "none",
        }),
      ]),
  });
};

export const useMutationUpdateTemperatureSensorTypeStatus = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: ({
      id,
      active,
    }: TemperatureSensorTypeStatusPayload) =>
      updateTemperatureSensorTypeStatus(id, { id, active }, language),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: QueryNames.temperatureSensorTypeList,
      }),
  });
};

export const useMutationDeleteTemperatureSensorType = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (id: string | number) =>
      deleteTemperatureSensorType(id, language),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: QueryNames.temperatureSensorTypeList,
      }),
  });
};
